import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../toast/toastContext";
import { ErrorState, LoadingState } from "../states";
import { getVendorColor } from "../charts/chartColors";
import { ToggleSwitch } from "../shared/ToggleSwitch";
import { VendorIcon } from "../consumption/VendorIcon";
import { formatWholeMoney } from "../consumption/formatters";
import { emptyEntry, isAmountAcceptable, isGroupSelectionMissing, isValidAmount, minimumAmount, toDraft, toItems, type Draft, type DraftEntry } from "../allocation/allocationDraft";
import { useAccessGroupCatalog, type AccessGroupCatalog } from "../allocation/useAccessGroupCatalog";
import { useAllocationForm } from "../allocation/useAllocationForm";
import {
  fetchMyPendingAllocationRequest,
  fetchMyProviderAllocation,
  requestMyProviderAllocation,
  withdrawMyRequest,
  type PendingAllocationRequest,
  type ProviderAllocationOverview,
  type ProviderAllocationState,
} from "../../api/providerAllocation";
import placeholder from "../states/Placeholder.module.css";
import { PlaceholderText } from "../states/PlaceholderText";
import card from "./ToolCard.module.css";
import styles from "./AllowanceCard.module.css";
import { unchangedOr, usePolling } from "../../hooks/usePolling";
import { Tooltip } from "../shared/Tooltip";

type LoadState = { kind: "loading" } | { kind: "loaded"; overview: ProviderAllocationOverview } | { kind: "error"; message: string };

/** What a provider costs under the draft: nothing when off, its fixed fee when flat-fee, otherwise the typed limit (0 until valid). */
function draftTotal(provider: ProviderAllocationState, entry: DraftEntry): number {
  if (!entry.enabled) {
    return 0;
  }
  if (!provider.supportsLimit) {
    return provider.minimumAmountUsd;
  }
  return isValidAmount(entry.amount) ? Number.parseInt(entry.amount, 10) : 0;
}

/**
 * The token allowance on My Tools, as an allowance planner: the monthly allowance with a bar split by
 * provider, one row per provider (its switch, its limit and what it has cost this month), and a
 * footer that sends the change to an approver. Nothing changes until the request is approved.
 *
 * The rules are the shared allocation draft's (`allocationDraft.ts`), the same ones the administrator's
 * form applies, so the figures and what may be submitted cannot differ between the two.
 */
export function AllowanceCard(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [pending, setPending] = useState<PendingAllocationRequest | null>(null);
  const overview = state.kind === "loaded" ? state.overview : null;
  const form = useAllocationForm(overview);
  const catalog = useAccessGroupCatalog();
  const { reset } = form;
  const [busy, setBusy] = useState(false);
  // What the card shows now, for a quiet refresh to compare against.
  const shown = useRef({ state, pending });
  shown.current = { state, pending };

  // A quiet load refreshes the card in place and keeps what is shown when it fails; it only runs while there is no unsent change.
  const load = useCallback(async (quiet = false): Promise<void> => {
    const [overviewResult, pendingResult] = await Promise.all([fetchMyProviderAllocation(), fetchMyPendingAllocationRequest()]);
    if (quiet && (overviewResult.kind !== "ok" || pendingResult.kind === "error")) {
      return;
    }
    if (overviewResult.kind !== "ok") {
      setState({ kind: "error", message: overviewResult.kind === "denied" ? t("myTools.allowance.denied") : overviewResult.message });
      return;
    }

    const requested = pendingResult.kind === "ok" ? pendingResult.response : null;
    // A request that only touches Bedrock cannot be shown here when Bedrock tooling is hidden from this person (AIA-109), so its chip is not shown either.
    const bedrockHidden = overviewResult.kind === "ok" && !overviewResult.response.providers.some((provider) => provider.vendorId.toLowerCase() === "bedrock");
    const pendingRequest = requested && bedrockHidden && requested.providers.length > 0 && requested.providers.every((item) => item.vendorId.toLowerCase() === "bedrock") ? null : requested;
    const nextState: LoadState = { kind: "loaded", overview: overviewResult.response };
    // An unchanged refresh leaves the card exactly as it was, draft included.
    if (quiet && unchangedOr(shown.current, { state: nextState, pending: pendingRequest }) === shown.current) {
      return;
    }

    setPending(pendingRequest);
    setState(nextState);
    reset(toDraft(overviewResult.response, pendingRequest?.providers));
  }, [reset, t]);

  useEffect(() => {
    void load();
  }, [load]);

  usePolling(() => load(true), state.kind === "loaded" && !form.changed && !busy);

  const submit = async (): Promise<void> => {
    if (!overview || !form.canSubmit) {
      return;
    }
    setBusy(true);
    const result = await requestMyProviderAllocation(toItems(overview, form.draft));
    setBusy(false);
    if (result.kind === "ok") {
      toast.show(t("myTools.allowance.submitted"), "success");
      await load();
    } else {
      toast.show(result.kind === "denied" ? t("myTools.allowance.denied") : result.message, "error");
    }
  };

  const withdraw = async (): Promise<void> => {
    if (!pending) {
      return;
    }
    setBusy(true);
    const result = await withdrawMyRequest(pending.id);
    setBusy(false);
    if (result.kind === "ok") {
      toast.show(t("myTools.allowance.withdrawn"), "success");
      await load();
    } else {
      toast.show(result.kind === "denied" ? t("myTools.allowance.denied") : result.message, "error");
    }
  };

  return (
    <section className={`${card.card} ${card.surface}`} aria-labelledby="allowance-heading">
      <div className={card.header}>
        <span className={card.headerIcon} aria-hidden="true">
          <AllowanceIcon />
        </span>
        <div className={card.headerText}>
          <h2 id="allowance-heading" className={card.title}>
            {t("myTools.allowance.title")}
          </h2>
          <p className={card.subtitle}>{t("myTools.allowance.intro")}</p>
        </div>
        {pending && <span className={`${card.chip} ${card.chipWarning}`}>{t("myTools.allowance.pendingChip")}</span>}
      </div>

      {state.kind === "loading" && <AllowanceLoading label={t("myTools.allowance.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {overview && form.figures && (
        <>
          <AllowanceSummary overview={overview} draft={form.draft} committed={form.figures.committed} allocated={form.figures.allocated} remaining={form.figures.remaining} />

          <ul className={styles.providers}>
            {overview.providers.map((provider, index) => (
              <ProviderRow
                key={provider.vendorId}
                provider={provider}
                colour={getVendorColor(provider.vendorId, index)}
                entry={form.draft[provider.vendorId] ?? emptyEntry}
                catalog={catalog}
                overCommitted={form.figures !== null && form.figures.remaining < 0}
                onChange={form.setEntry}
              />
            ))}
          </ul>

          <div className={styles.footer}>
            <p className={styles.footerNote} aria-live="polite">
              {pending ? t("myTools.allowance.pendingNote") : form.changed ? t("myTools.allowance.changedNote") : t("myTools.allowance.unchangedNote")}
            </p>
            <div className={styles.footerActions}>
              {pending && (
                <button type="button" className="button button-secondary" onClick={() => void withdraw()} disabled={busy}>
                  {t("myTools.allowance.cancelRequest")}
                </button>
              )}
              <button type="button" className="button button-primary" onClick={() => void submit()} disabled={busy || !form.canSubmit}>
                {pending ? t("myTools.allowance.updateRequest") : t("myTools.allowance.requestChange")}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** How many provider rows the loading layout sketches. */
const PlaceholderProviderCount = 3;

/**
 * The allowance card while its figures load: the summary box built from the same parts as the loaded one, with the
 * spinner where the allowance will be and sample text, drawn as soft bars, where the labels and figures will be, and
 * placeholder provider rows, so the card is the same height before and after and nothing moves when the figures arrive.
 *
 * @param props.label - What is loading, shown beside the spinner.
 */
function AllowanceLoading({ label }: { label: string }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div aria-busy="true">
      <div className={styles.summary}>
        <div className={styles.summaryTop}>
          <div>
            <p className={`panel-title ${styles.totalLabel}`} aria-hidden="true">
              <PlaceholderText>{t("myTools.allowance.totalLabel")}</PlaceholderText>
            </p>
            {/* The allowance's line, at its own height, with the spinner in it. */}
            <div className={styles.total}>
              <span className={styles.loadingSlot}>
                <span className={styles.loadingStatus}>
                  <LoadingState label={label} />
                </span>
              </span>
            </div>
          </div>
          <dl className={styles.figures} aria-hidden="true">
            <div>
              <dt>
                <PlaceholderText>{t("myTools.allowance.allocated")}</PlaceholderText>
              </dt>
              <dd>
                <PlaceholderText>$250</PlaceholderText>
              </dd>
            </div>
            <div>
              <dt>
                <PlaceholderText>{t("myTools.allowance.remaining")}</PlaceholderText>
              </dt>
              <dd>
                <PlaceholderText>$150</PlaceholderText>
              </dd>
            </div>
          </dl>
        </div>
        <div className={`${styles.bar} ${placeholder.pulse}`} aria-hidden="true" />
      </div>
      <ul className={styles.providers} aria-hidden="true">
        {Array.from({ length: PlaceholderProviderCount }, (_, index) => (
          <li key={index} className={`${styles.provider} ${styles.providerOff}`}>
            <div className={styles.providerMain}>
              <span className={`${styles.providerIcon} ${placeholder.pulse}`} />
              <div className={styles.loadingText}>
                <div className={`${placeholder.block} ${placeholder.line} ${styles.loadingName}`} />
                <div className={`${placeholder.block} ${placeholder.line} ${styles.loadingDetail}`} />
              </div>
              <span className={`${placeholder.block} ${styles.loadingSwitch}`} />
              <span className={`${placeholder.block} ${styles.loadingAmount}`} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The monthly allowance and a bar split by provider under the draft, so a change shows its effect as it is typed: each switched-on
 * provider's share in its colour, cost already spent on switched-off providers hatched, and what is left unfilled.
 */
function AllowanceSummary({
  overview,
  draft,
  committed,
  allocated,
  remaining,
}: {
  overview: ProviderAllocationOverview;
  draft: Draft;
  committed: number;
  allocated: number;
  remaining: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const scale = Math.max(overview.totalUsd, allocated + committed, 1);
  const over = remaining < 0;

  return (
    <div className={styles.summary}>
      <div className={styles.summaryTop}>
        <div>
          <p className={`panel-title ${styles.totalLabel}`}>{t("myTools.allowance.totalLabel")}</p>
          <p className={styles.total}>{formatWholeMoney(overview.totalUsd, "USD")}</p>
        </div>
        <dl className={styles.figures}>
          <div>
            <dt>{t("myTools.allowance.allocated")}</dt>
            <dd>{formatWholeMoney(allocated, "USD")}</dd>
          </div>
          {committed > 0 && (
            <div>
              <dt>{t("myTools.allowance.committed")}</dt>
              <dd>{formatWholeMoney(committed, "USD")}</dd>
            </div>
          )}
          <div className={over ? styles.figureOver : styles.figureRemaining}>
            <dt>{over ? t("myTools.allowance.over") : t("myTools.allowance.remaining")}</dt>
            <dd>{formatWholeMoney(Math.abs(remaining), "USD")}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.bar} aria-hidden="true">
        {overview.providers.map((provider, index) => {
          const value = draftTotal(provider, draft[provider.vendorId] ?? emptyEntry);
          return value > 0 ? (
            <Tooltip
              key={provider.vendorId}
              label={`${provider.displayName}: ${formatWholeMoney(value, "USD")}`}
              className={styles.segmentWrap}
              style={{ width: `${String((value / scale) * 100)}%` }}
            >
              <span className={styles.segment} style={{ "--segment-colour": getVendorColor(provider.vendorId, index) } as React.CSSProperties} />
            </Tooltip>
          ) : null;
        })}
        {committed > 0 && <span className={`${styles.segment} ${styles.segmentCommitted}`} style={{ width: `${String((committed / scale) * 100)}%` }} />}
      </div>

      {over && (
        <p role="alert" className={styles.overAlert}>
          {t("myTools.allowance.overAlert", { over: formatWholeMoney(-remaining, "USD") })}
        </p>
      )}
    </div>
  );
}

/** One provider: its name and fee, what it has cost this month, its switch and its limit, and (when on) its access groups. */
function ProviderRow({
  provider,
  colour,
  entry,
  catalog,
  overCommitted,
  onChange,
}: {
  provider: ProviderAllocationState;
  colour: string;
  entry: DraftEntry;
  catalog: AccessGroupCatalog;
  overCommitted: boolean;
  onChange: (vendorId: string, change: Partial<DraftEntry>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const id = `allowance-${provider.vendorId}`;
  const minimum = minimumAmount(provider);
  const editable = entry.enabled && provider.supportsLimit;
  const invalid = editable && !isAmountAcceptable(provider, entry.amount);
  const offered = useMemo(() => catalog[provider.vendorId] ?? [], [catalog, provider.vendorId]);
  const showGroups = provider.supportsAccessGroups === true && entry.enabled && offered.length !== 1;
  const groupsMissing = isGroupSelectionMissing(provider, entry);
  const spent = provider.incurredCostUsd ?? 0;
  const limit = draftTotal(provider, entry);
  const spentShare = limit > 0 ? Math.min(100, (spent / limit) * 100) : 0;

  // A provider granted per access group that offers exactly one group shows no choice: switching it on grants that group.
  useEffect(() => {
    if (provider.supportsAccessGroups === true && offered.length === 1 && entry.enabled && (entry.accessGroups.length !== 1 || entry.accessGroups[0] !== offered[0].id)) {
      onChange(provider.vendorId, { accessGroups: [offered[0].id] });
    }
  }, [provider, offered, entry, onChange]);

  let detail: string;
  if (!entry.enabled) {
    detail = spent > 0 ? t("myTools.allowance.offSpent", { spent: formatWholeMoney(spent, "USD") }) : t("myTools.allowance.off");
  } else if (!provider.supportsLimit) {
    detail = t("myTools.allowance.flatFee", { amount: formatWholeMoney(provider.seatFeeUsd, "USD") });
  } else {
    detail = t("myTools.allowance.spentOf", { spent: formatWholeMoney(spent, "USD"), limit: formatWholeMoney(limit, "USD") });
  }

  return (
    <li className={entry.enabled ? styles.provider : `${styles.provider} ${styles.providerOff}`} style={{ "--provider-colour": colour } as React.CSSProperties}>
      <div className={styles.providerMain}>
        <span className={styles.providerIcon} aria-hidden="true">
          <VendorIcon vendorId={provider.vendorId.toLowerCase()} />
        </span>
        <div className={styles.providerText}>
          <p id={`${id}-name`} className={styles.providerName}>
            {provider.displayName}
            {entry.enabled && provider.supportsLimit && provider.seatFeeUsd > 0 && (
              <span className={styles.providerFee}>{t("myTools.allowance.seatFee", { amount: formatWholeMoney(provider.seatFeeUsd, "USD") })}</span>
            )}
          </p>
          <p className={styles.providerDetail}>{detail}</p>
          {editable && limit > 0 && (
            <span className={styles.spentBar} aria-hidden="true">
              <span style={{ width: `${String(spentShare)}%` }} />
            </span>
          )}
        </div>
        <span id={`${id}-switch-label`} className="visually-hidden">
          {t("myTools.allowance.enable", { provider: provider.displayName })}
        </span>
        <ToggleSwitch
          checked={entry.enabled}
          labelledBy={`${id}-switch-label`}
          onChange={(checked) =>
            onChange(provider.vendorId, {
              enabled: checked,
              ...(checked && provider.supportsLimit
                ? { amount: String(Math.max(minimum, isValidAmount(entry.amount) ? Number.parseInt(entry.amount, 10) : 0)) }
                : {}),
            })
          }
        />
        <div className={editable ? styles.amount : `${styles.amount} ${styles.amountReadOnly}`}>
          <span aria-hidden="true">$</span>
          {editable ? (
            <input
              id={`${id}-amount`}
              type="number"
              inputMode="numeric"
              min={minimum}
              step={1}
              value={entry.amount}
              aria-label={t("myTools.allowance.amount", { provider: provider.displayName })}
              aria-invalid={invalid || overCommitted}
              aria-describedby={invalid ? `${id}-error` : undefined}
              onChange={(event) => onChange(provider.vendorId, { amount: event.target.value })}
            />
          ) : (
            <input
              id={`${id}-amount`}
              type="text"
              readOnly
              tabIndex={-1}
              value={entry.enabled ? String(provider.seatFeeUsd) : "0"}
              aria-label={
                entry.enabled
                  ? t("myTools.allowance.fixedAmount", { provider: provider.displayName })
                  : t("myTools.allowance.offAmount", { provider: provider.displayName })
              }
            />
          )}
        </div>
      </div>

      {invalid && (
        <p id={`${id}-error`} className={styles.error}>
          {isValidAmount(entry.amount)
            ? t("myTools.allowance.belowMinimum", { minimum: formatWholeMoney(minimum, "USD") })
            : t("myTools.allowance.notWhole")}
        </p>
      )}

      {showGroups && (
        <fieldset className={styles.groups} aria-describedby={groupsMissing ? `${id}-groups-error` : undefined}>
          <legend>{t("accessGroups.legend", { provider: provider.displayName })}</legend>
          <div className={styles.groupOptions}>
            {offered.map((group) => (
              <label key={group.id} className={styles.groupOption}>
                <input
                  type="checkbox"
                  checked={entry.accessGroups.includes(group.id)}
                  onChange={(event) =>
                    onChange(provider.vendorId, {
                      accessGroups: event.target.checked ? [...entry.accessGroups, group.id] : entry.accessGroups.filter((groupId) => groupId !== group.id),
                    })
                  }
                />
                <span>
                  <span className={styles.groupName}>{group.displayName}</span>
                  {group.description !== "" && <span className={styles.groupDescription}>{group.description}</span>}
                </span>
              </label>
            ))}
          </div>
          {groupsMissing && (
            <p id={`${id}-groups-error`} className={styles.error}>
              {t("accessGroups.required")}
            </p>
          )}
        </fieldset>
      )}
    </li>
  );
}

/** A stack of coins, for the allowance card's header. */
function AllowanceIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.66 3.13 3 7 3s7-1.34 7-3V6" />
      <path d="M5 12v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
    </svg>
  );
}
